import { SCENE_GRAPH_TOP_PADDING, DEFAULT_ZOOM } from "./constants";

/**
 * Compute the bounding box of the diagram content for viewport fitting.
 * Returns null when there are no nodes (caller should treat the SVG as empty).
 *
 * Padding asymmetry is intentional: extra room on the right makes labels readable,
 * extra room on the top matches the SCENE_GRAPH_TOP_PADDING used during layout
 * so the world-cameras row never gets clipped.
 */
export function computeContentBounds(nodes) {
  if (!nodes?.length) return null;
  const minX = Math.min(...nodes.map((n) => n.x)) - 60;
  const maxX = Math.max(...nodes.map((n) => n.x)) + 200;
  const minY = Math.min(...nodes.map((n) => n.y)) - SCENE_GRAPH_TOP_PADDING;
  const maxY = Math.max(...nodes.map((n) => n.y)) + 80;
  const cw = maxX - minX;
  const ch = maxY - minY;
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  return { cw, ch, cx, cy };
}

/**
 * Format an SVG `viewBox` string from content bounds + pan/zoom.
 * Falls back to a simple "0 0 W H" frame when bounds are unavailable.
 */
export function buildSceneGraphViewBox(contentBounds, panValue, zoomValue, width, height) {
  if (!contentBounds) return `0 0 ${width} ${height}`;
  const vw = contentBounds.cw / zoomValue;
  const vh = contentBounds.ch / zoomValue;
  const vx = (contentBounds.cx - contentBounds.cw / 2 - panValue.x) / zoomValue;
  const vy = (contentBounds.cy - contentBounds.ch / 2 - panValue.y) / zoomValue;
  return `${vx} ${vy} ${vw} ${vh}`;
}

/**
 * Pick an initial zoom that fills the panel comfortably.
 *
 * The auto-fit deliberately aims for a *partial* fill (`targetSecondaryOccupancy`
 * is below 1) so the first frame of a new graph never feels magnified. The cap
 * and floor scale with body count so tiny graphs zoom in further than huge ones,
 * and `zoomFloor` must stay < 1 for small graphs — otherwise `suggestedZoom`
 * could never zoom out when the content is compact relative to the panel.
 */
export function computeSceneGraphAutoFitZoom(nodes, width, height) {
  const minX = Math.min(...nodes.map((n) => n.x)) - 60;
  const maxX = Math.max(...nodes.map((n) => n.x)) + 200;
  const minY = Math.min(...nodes.map((n) => n.y)) - SCENE_GRAPH_TOP_PADDING;
  const maxY = Math.max(...nodes.map((n) => n.y)) + 80;
  const contentWidth = Math.max(1, maxX - minX);
  const contentHeight = Math.max(1, maxY - minY);

  const fitScale = Math.min(
    Math.max(0.0001, width / contentWidth),
    Math.max(0.0001, height / contentHeight)
  );
  const occupancyX = (contentWidth * fitScale) / Math.max(1, width);
  const occupancyY = (contentHeight * fitScale) / Math.max(1, height);
  const secondaryOccupancy = Math.max(0.0001, Math.min(occupancyX, occupancyY));
  const bodyCount = nodes.reduce((count, node) => count + (node.type === "body" ? 1 : 0), 0);
  const targetSecondaryOccupancy =
    bodyCount >= 12 ? 0.6 : bodyCount >= 6 ? 0.66 : bodyCount >= 3 ? 0.64 : 0.58;
  const suggestedZoom = targetSecondaryOccupancy / secondaryOccupancy;
  const zoomCap = bodyCount <= 2 ? 1.2 : bodyCount >= 12 ? 2.05 : 2.25;
  const zoomFloor = bodyCount >= 6 ? 1.08 : 0.72;
  return Math.min(
    zoomCap,
    Math.max(zoomFloor, Number.isFinite(suggestedZoom) ? suggestedZoom : DEFAULT_ZOOM)
  );
}
